"use client"
import type React from "react"
import { useState, useEffect } from "react"
import { Link } from "react-router-dom"
import { getAllHolidays, deleteHoliday } from "api/apiHelper" // Update with correct path
import { usePermissions } from "app/hooks"
import { toast } from "react-toastify"
import TabsContainer from "components/TabsContainer"
import Tab from "components/Tab"

interface Holiday {
  id: number
  name: string
  date: string
}

const AllHolidays: React.FC = () => {
  const [holidays, setHolidays] = useState<Holiday[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [deleteModal, setDeleteModal] = useState<{ isOpen: boolean; holiday: Holiday | null }>({
    isOpen: false,
    holiday: null,
  })
  const [deleting, setDeleting] = useState(false)
  const [filterType, setFilterType] = useState<string | null>(null)

  const permissions = usePermissions() || []

  useEffect(() => {
    const fetchHolidays = async () => {
      try {
        setLoading(true)
        const response = await getAllHolidays()
        setHolidays(response.data)
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to fetch holidays")
      } finally {
        setLoading(false)
      }
    }
    fetchHolidays()
  }, [])

  // Helper functions
  const today = new Date()
  today.setHours(0, 0, 0, 0)

  const isToday = (dateString: string) => {
    const holidayDate = new Date(dateString)
    holidayDate.setHours(0, 0, 0, 0)
    return holidayDate.getTime() === today.getTime()
  }

  const isUpcoming = (dateString: string) => {
    const holidayDate = new Date(dateString)
    holidayDate.setHours(0, 0, 0, 0)
    return holidayDate.getTime() >= today.getTime()
  }

  const isPast = (dateString: string) => {
    const holidayDate = new Date(dateString)
    holidayDate.setHours(0, 0, 0, 0)
    return holidayDate.getTime() < today.getTime()
  }

  // Filter and sort holidays
  const getFilteredHolidays = () => {
    let filtered = [...holidays]

    switch (filterType) {
      case "upcoming":
        filtered = filtered.filter((holiday) => isUpcoming(holiday.date))
        break
      case "past":
        filtered = filtered.filter((holiday) => isPast(holiday.date))
        break
      case "today":
        filtered = filtered.filter((holiday) => isToday(holiday.date))
        break
      default:
        // All holidays
        break
    }

    return filtered.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
  }

  const filteredAndSortedHolidays = getFilteredHolidays()

  // Format date to be more readable
  const formatDate = (dateString: string) => {
    const options: Intl.DateTimeFormatOptions = {
      month: "long",
      day: "numeric",
      year: "numeric",
    }
    return new Date(dateString).toLocaleDateString(undefined, options)
  }

  // Calculate days until holiday
  const daysUntil = (dateString: string) => {
    const holidayDate = new Date(dateString)
    holidayDate.setHours(0, 0, 0, 0)
    const diffTime = holidayDate.getTime() - today.getTime()
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))

    if (diffDays === 0) return "Today"
    if (diffDays === 1) return "Tomorrow"
    if (diffDays > 0) return `${diffDays} days`
    if (diffDays === -1) return "Yesterday"
    return `${Math.abs(diffDays)} days ago`
  }

  const getHolidayStatus = (dateString: string) => {
    if (isToday(dateString)) return "today"
    if (isUpcoming(dateString)) return "upcoming"
    return "past"
  }

  const getStatusStyles = (status: string) => {
    switch (status) {
      case "today":
        return {
          bg: "bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-900/30",
          icon: "text-green-600 dark:text-green-400",
          iconBg: "bg-green-200 dark:bg-green-800",
          badge: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300",
          days: "text-green-600 dark:text-green-400",
        }
      case "upcoming":
        return {
          bg: "bg-blue-50 dark:bg-blue-900/20",
          icon: "text-blue-600 dark:text-blue-400",
          iconBg: "bg-blue-200 dark:bg-blue-800",
          badge: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300",
          days: "text-blue-600 dark:text-blue-400",
        }
      case "past":
      default:
        return {
          bg: "bg-gray-50 dark:bg-gray-800/50",
          icon: "text-gray-500 dark:text-gray-400",
          iconBg: "bg-gray-200 dark:bg-gray-700",
          badge: "bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300",
          days: "text-gray-500 dark:text-gray-400",
        }
    }
  }

  const handleDeleteClick = (holiday: Holiday) => {
    setDeleteModal({ isOpen: true, holiday })
  }

  const handleDeleteConfirm = async () => {
    if (!deleteModal.holiday) return

    try {
      setDeleting(true)
      await deleteHoliday(deleteModal.holiday.id)
      setHolidays((prev) => prev.filter((h) => h.id !== deleteModal.holiday!.id))
      setDeleteModal({ isOpen: false, holiday: null })
      toast.success(`"${deleteModal.holiday.name}" has been deleted successfully`)
    } catch (err) {
      console.error("Failed to delete holiday:", err)
      toast.error(
        err instanceof Error && err.message
          ? `Failed to delete holiday: ${err.message}`
          : "Failed to delete holiday. Please try again.",
      )
    } finally {
      setDeleting(false)
    }
  }

  const handleDeleteCancel = () => {
    setDeleteModal({ isOpen: false, holiday: null })
  }

  // Component to render holidays list
  const HolidaysList = ({ holidays }: { holidays: Holiday[] }) => (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
      <div className="p-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">
          {filterType === null
            ? "All Holidays"
            : filterType === "upcoming"
              ? "Upcoming Holidays"
              : filterType === "past"
                ? "Past Holidays"
                : "Today's Holidays"}
        </h2>

        {/* Holidays List */}
        <div className="space-y-4">
          {holidays.map((holiday, index) => {
            const status = getHolidayStatus(holiday.date)
            const statusStyles = getStatusStyles(status)
            const days = daysUntil(holiday.date)

            return (
              <div
                key={holiday.id}
                className={`flex items-center justify-between p-4 rounded-lg border border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors ${
                  index % 2 === 0 ? "bg-gray-50/50 dark:bg-gray-800/50" : "bg-white dark:bg-gray-800"
                } ${statusStyles.bg}`}
              >
                <div className="flex items-center space-x-4 flex-1">
                  <div className={`p-2 rounded-full ${statusStyles.iconBg}`}>
                    <i className={`fas fa-calendar-day ${statusStyles.icon}`}></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-3 mb-1">
                      <h3 className="text-base font-semibold text-gray-900 dark:text-white truncate">{holiday.name}</h3>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${statusStyles.badge}`}>
                        {status === "today" ? "Today" : status === "upcoming" ? "Upcoming" : "Past"}
                      </span>
                    </div>
                    <div className="flex items-center text-sm text-gray-600 dark:text-gray-300">
                      <i className="fas fa-calendar-alt mr-2"></i>
                      {formatDate(holiday.date)}
                    </div>
                  </div>
                  <div className={`text-sm font-medium ${statusStyles.days}`}>{days}</div>
                </div>
                {permissions && permissions.includes("bulletin.delete_holiday") && (
                  <button
                    onClick={() => handleDeleteClick(holiday)}
                    className="ml-4 p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                    title="Delete holiday"
                  >
                    <i className="fas fa-trash text-sm"></i>
                  </button>
                )}
              </div>
            )
          })}
        </div>

        {/* Empty State */}
        {holidays.length === 0 && (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="fas fa-calendar-times text-2xl text-gray-400"></i>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              {filterType === null
                ? "No holidays yet"
                : filterType === "upcoming"
                  ? "No upcoming holidays"
                  : filterType === "past"
                    ? "No past holidays"
                    : "No holidays today"}
            </h3>
            <p className="text-gray-500 dark:text-gray-400 mb-6">
              {filterType === null
                ? "Get started by creating your first holiday."
                : filterType === "upcoming"
                  ? "All clear ahead — no holidays coming up!"
                  : filterType === "past"
                    ? "No past holidays to display."
                    : "No holidays scheduled for today."}
            </p>
            {permissions && permissions.includes("bulletin.add_holiday") && (
              <Link
                to="/bulletin/holidays/create/"
                className="inline-flex items-center px-4 py-2 bg-brand-500 text-white rounded-lg hover:bg-brand-400 transition-colors"
              >
                <i className="fas fa-plus mr-2"></i>
                Create Holiday
              </Link>
            )}
          </div>
        )}
      </div>
    </div>
  )

  if (loading) {
    return (
      <div className="container mt-5">
        <div className="flex items-center justify-center py-16">
          <div className="text-center">
            <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="fas fa-spinner fa-spin text-blue-600 dark:text-blue-400"></i>
            </div>
            <p className="text-gray-600 dark:text-gray-300">Loading holidays...</p>
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="container mt-5">
        <div className="flex items-center justify-center py-16">
          <div className="text-center max-w-md">
            <div className="w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="fas fa-exclamation-triangle text-red-600 dark:text-red-400"></i>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Something went wrong</h3>
            <p className="text-gray-600 dark:text-gray-300 mb-6">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="container mt-5">
        {/* Header */}
        <div className="flex items-center justify-end">
          {/* <div className="flex items-center space-x-4">
            <Link
              to="/"
              className="flex items-center justify-center w-10 h-10 rounded-full bg-white dark:bg-gray-800 shadow-sm hover:shadow-md transition-shadow"
            >
              <i className="fas fa-arrow-left text-gray-600 dark:text-gray-400"></i>
            </Link>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Dashboard</h1>
              <p className="text-gray-500 dark:text-gray-400 mt-1">{filteredAndSortedHolidays.length} holidays</p>
            </div>
          </div> */}
          {permissions && permissions.includes("bulletin.add_holiday") && (
            <Link
              to="/bulletin/holidays/create/"
              className="flex items-center px-4 py-2 bg-brand-500 text-white hover:bg-brand-600 dark:bg-brand-400 dark:hover:bg-brand-500 rounded-lg transition-colors shadow-sm"
            >
              <i className="fas fa-plus mr-2"></i>
              New Holiday
            </Link>
          )}
        </div>

        {/* Tabs using TabsContainer and Tab components */}
        <TabsContainer>
          <Tab isActive={filterType === null} title="All" onClick={() => setFilterType(null)}>
            <HolidaysList holidays={holidays.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())} />
          </Tab>
          <Tab isActive={filterType === "upcoming"} title="Upcoming" onClick={() => setFilterType("upcoming")}>
            <HolidaysList
              holidays={holidays
                .filter((h) => isUpcoming(h.date))
                .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())}
            />
          </Tab>
          <Tab isActive={filterType === "past"} title="Past" onClick={() => setFilterType("past")}>
            <HolidaysList
              holidays={holidays
                .filter((h) => isPast(h.date))
                .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())}
            />
          </Tab>
          <Tab isActive={filterType === "today"} title="Today" onClick={() => setFilterType("today")}>
            <HolidaysList
              holidays={holidays
                .filter((h) => isToday(h.date))
                .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())}
            />
          </Tab>
        </TabsContainer>

      {/* Delete Confirmation Modal */}
      {deleteModal.isOpen && deleteModal.holiday && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mr-4">
                  <i className="fas fa-exclamation-triangle text-red-600 dark:text-red-400"></i>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Delete Holiday</h3>
                  <p className="text-gray-500 dark:text-gray-400 text-sm">This action cannot be undone</p>
                </div>
              </div>
              <p className="text-gray-600 dark:text-gray-300 mb-6">
                Are you sure you want to delete "{deleteModal.holiday.name}"?
              </p>
              <div className="flex space-x-3">
                <button
                  onClick={handleDeleteCancel}
                  disabled={deleting}
                  className="flex-1 px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteConfirm}
                  disabled={deleting}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center justify-center"
                >
                  {deleting ? (
                    <>
                      <i className="fas fa-spinner fa-spin mr-2"></i>
                      Deleting...
                    </>
                  ) : (
                    "Delete"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default AllHolidays
